import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Landmark, HeartPulse, Baby, Briefcase, Home, Search, ArrowRight, X } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TEMA = [
  {
    id: "okonomi",
    label: "Kommunen og økonomien",
    icon: Landmark,
    beskriving: "Gjeld, driftsresultat, fond, eigedomsskatt og rentekostnader",
  },
  {
    id: "helse",
    label: "Helse og omsorg",
    icon: HeartPulse,
    beskriving: "Fastlegedekning, bemanning i omsorga og framskriving av folketalet",
  },
  {
    id: "barnunge",
    label: "Barn og unge",
    icon: Baby,
    beskriving: "Barnehage og skule, alderssamansetjing og barnefattigdom",
  },
  {
    id: "arbeidsliv",
    label: "Folk og arbeidsliv",
    icon: Briefcase,
    beskriving: "Sysselsetjing, ytingar, inntekt, pendling og næringar",
  },
  {
    id: "bustad",
    label: "Bustad og hushaldsøkonomi",
    icon: Home,
    beskriving: "Bustadprisar, leige, kommunale gebyr, livsopphald og levekostnader",
  },
];

export const TemaVelgar = () => {
  const navigate = useNavigate();
  const [valt, setValt] = useState(null);
  const [kommunar, setKommunar] = useState([]);
  const [sok, setSok] = useState("");

  useEffect(() => {
    axios.get(`${API}/kommuner`).then((r) => setKommunar(r.data)).catch(() => setKommunar([]));
  }, []);

  const treff = useMemo(() => {
    const q = sok.trim().toLowerCase();
    if (!q) return [];
    return kommunar.filter((k) => k.kommune_name.toLowerCase().includes(q)).slice(0, 8);
  }, [sok, kommunar]);

  const opne = (kommuneCode) => navigate(`/kommune/${kommuneCode}?tema=${valt.id}`);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" data-testid="tema-velgar">
      <div className="text-center mb-10">
        <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-[#1F2A36] mb-4">Gå rett til eit tema</h2>
        <p className="text-[#4B647F] max-w-xl mx-auto">
          Vel temaet du er oppteken av, og hopp rett ned i tala for kommunen din
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {TEMA.map((t) => {
          const aktiv = valt?.id === t.id;
          return (
            <button
              key={t.id}
              onClick={() => {
                setValt(aktiv ? null : t);
                setSok("");
              }}
              data-testid={`tema-kort-${t.id}`}
              className={`text-left rounded-lg border p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm ${
                aktiv ? "border-[#103766] bg-[#103766] text-white" : "border-[#E6E9F0] bg-white"
              }`}
            >
              <t.icon
                className={`w-6 h-6 mb-3 ${aktiv ? "text-white" : "text-[#103766]"}`}
                strokeWidth={1.5}
              />
              <p className={`text-base font-medium mb-1 ${aktiv ? "text-white" : "text-[#1F2A36]"}`}>{t.label}</p>
              <p className={`text-sm leading-relaxed ${aktiv ? "text-white/80" : "text-[#4B647F]"}`}>{t.beskriving}</p>
            </button>
          );
        })}
      </div>

      {valt && (
        <div className="mt-6 max-w-xl mx-auto" data-testid="tema-kommunevel">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-[#4B647F]">
              Kva kommune vil du sjå <span className="font-medium text-[#1F2A36]">{valt.label}</span> for?
            </p>
            <button
              onClick={() => setValt(null)}
              className="text-xs text-[#8AA4BF] hover:text-[#103766] inline-flex items-center gap-1"
              data-testid="tema-lukk"
            >
              <X className="w-3 h-3" /> Lukk
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8AA4BF]" />
            <Input
              autoFocus
              value={sok}
              onChange={(e) => setSok(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && treff[0]) opne(treff[0].kommune_code);
              }}
              placeholder="Søk etter kommune …"
              className="pl-9 bg-white border-[#E6E9F0]"
              data-testid="tema-sok"
            />
          </div>
          {treff.length > 0 && (
            <div className="mt-2 rounded-lg border border-[#E6E9F0] bg-white overflow-hidden" data-testid="tema-treff">
              {treff.map((k) => (
                <button
                  key={k.kommune_code}
                  onClick={() => opne(k.kommune_code)}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-left text-sm text-[#1F2A36] hover:bg-[#F1F4F8] transition-colors"
                  data-testid={`tema-treff-${k.kommune_code}`}
                >
                  <span>
                    {k.kommune_name} <span className="text-[#8AA4BF]">· {k.fylke_name}</span>
                  </span>
                  <ArrowRight className="w-4 h-4 text-[#8AA4BF]" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TemaVelgar;
